import { useEffect, useRef, useCallback } from "react";

import { motion } from "framer-motion";
import { createConstellations, createStars, createParticles, createLightRays } from "../../Data/Functions";
import { TypeAnimation } from 'react-type-animation';
import {
  FaLaptopCode,
  FaDatabase,
  FaPalette,
  FaCode,
  FaCogs,
  FaCodeBranch
} from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { FaXTwitter } from "react-icons/fa6";
import { FiMail, FiChevronDown } from "react-icons/fi";
import "../Styles/Home.css";

function Home() {
  const scrollYRef = useRef(0);
  const gridRef = useRef(null);
  const radialRef = useRef(null);
  const nebulaRefs = useRef([]);
  const rafId = useRef(null);



  // Throttled scroll handler using rAF - updates DOM directly, no setState
  const handleScroll = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      const sy = window.scrollY;
      scrollYRef.current = sy;

      if (gridRef.current) {
        gridRef.current.style.transform = `translateY(${sy * 0.02}px)`;
      }
      if (radialRef.current) {
        radialRef.current.style.transform = `translateY(${sy * 0.05}px)`;
      }

      // Update nebula parallax positions  
      const parallaxData = [
        { dx: -0.01, dy: 0.02 },
        { dx: 0.01, dy: -0.03 },
        { dx: 0, dy: 0.04 },
        { dx: 0.02, dy: -0.02 },
        { dx: -0.02, dy: 0.03 },
      ];
      nebulaRefs.current.forEach((el, i) => {
        if (el && parallaxData[i]) {
          const { dx, dy } = parallaxData[i];
          el.style.transform = `translateY(${sy * dy}px) translateX(${sy * dx}px)`;
        }
      });

      rafId.current = null;
    });
  }, []);

  useEffect(() => {
    const createShootingStar = () => {
      const shootingStar = document.createElement('div');
      shootingStar.className = 'shooting-star';
      shootingStar.style.left = Math.random() * 100 + '%';
      shootingStar.style.top = Math.random() * 50 + '%';
      const container = document.querySelector('.stars-container');
      if (container) {
        container.appendChild(shootingStar);
        setTimeout(() => {
          shootingStar.remove();
        }, 2000);
      }
    };

    const shootingStarInterval = setInterval(createShootingStar, 5000);

    createStars();
    createParticles();
    createLightRays();
    createConstellations();

    // Debounce resize - don't recreate on every pixel
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        createStars();
        createParticles();
        createLightRays();
        createConstellations();
      }, 300);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearInterval(shootingStarInterval);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [handleScroll]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen text-gray-700 dark:text-gray-300 text-center p-5 md:px-12 pt-32 md:pt-40 md:pb-12 relative overflow-hidden"
    >
      {/* Main content */}
      <div className="relative z-10">
        <div className="relative z-10">
          <div className="relative inline-block">
            <motion.img
              src="/new22.jpg"
              alt="Piyush Joshi"
              className="w-56 sm:w-48 h-w-56 sm:h-48 md:w-64 md:h-64 rounded-full border-[2px] border-gray-200 dark:border-slate-600 mx-auto shadow-xl transform -translate-y-4 transition-all duration-500 hover:scale-105  z-[1000]"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              loading="eager"
            />
          </div>

          <motion.h1
            className="text-2xl sm:text-4xl md:text-5xl mt-3 sm:mt-8 font-extrabold tracking-tight "
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.54, delay: 0.45 }}
          >
            <span className="text-slate-700 dark:text-slate-200">Piyush</span>{" "}
            <span className="text-gray-900 dark:text-white">
              Joshi
            </span>
          </motion.h1>

          <motion.div
            className="text-lg md:text-xl mt-1 sm:mt-4 text-slate-600 dark:text-slate-400 font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <TypeAnimation
              sequence={[
                'Full Stack Developer',
                3000,
                'Tech Enthusiast',
                3000,
                'Problem Solver',
                3000,
                'Innovation Driver',
                3000,
              ]}
              wrapper="span"
              className="typical-text"
              speed={40}
              repeat={Infinity}
              cursor={true}
              deletionSpeed={40}
            />
          </motion.div>

          <motion.div
            className="flex items-center justify-center transform pt-4 sm:pb-2 sm:pt-5 -translate-x-1/2 bottom-[-40px] animate-bounce opacity-70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <FiChevronDown className="text-gray-600 dark:text-gray-400 text-2xl" />
          </motion.div>
        </div>
      </div>

      {/* Bio section */}
      <motion.div
        className="md:max-w-3xl mx-auto text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-5 sm:mb-16 p-1 backdrop-blur-md z-10 relative overflow-hidden group"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <div className="relative z-10">
          <motion.div
            className="w-12 h-1 bg-gray-800 dark:bg-gray-300 rounded-full mb-2 mx-auto sm:mx-0"
            initial={{ width: 0 }}
            animate={{ width: 48 }}
            transition={{ duration: 0.8, delay: 1 }}
          />

          <p className="mb-4 text-justify text-gray-700 dark:text-gray-300 leading-relaxed">
            <span className="text-gray-900 dark:text-white font-semibold">Welcome!</span> I'm a Full Stack Developer from Hisar, Haryana,
            specializing in building responsive, scalable, and efficient web
            solutions. I completed my <span className="text-gray-900 dark:text-white font-medium">Bachelors of Technology (2026) in Computer Science</span> at{" "}
            <span className="text-gray-900 dark:text-white font-medium">Netaji Subhas University Of Technology (NSUT)</span>, Dwarka,
            Delhi, and I am passionate about crafting innovative applications that
            enhance user experiences.
          </p>

          <div className="absolute top-0 right-0 w-32 h-32 opacity-5 pointer-events-none">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-400" />
              <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-400" />
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-400" />
            </svg>
          </div>
        </div>
      </motion.div>

      {/* Social links */}
      <motion.div
        className="flex gap-8 justify-center text-lg sm:text-2xl mb-5 sm:mb-10 z-10 relative"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {[
          {
            icon: <img src="https://raw.githubusercontent.com/devicons/devicon/master/icons/linkedin/linkedin-original.svg" alt="LinkedIn" className="w-6 h-6 sm:w-7 sm:h-7" />,
            link: "https://www.linkedin.com/in/piyush-joshi-324778254/",
            label: "LinkedIn",
            colorClass: "",
            hoverColor: "",
            bgColor: "hover:bg-[#0a66c2]/10"
          },
          {
            icon: <img src="https://raw.githubusercontent.com/devicons/devicon/master/icons/github/github-original.svg" alt="GitHub" className="w-6 h-6 sm:w-7 sm:h-7 dark:invert" />,
            link: "https://github.com/Piyush0049",
            label: "GitHub",
            colorClass: "",
            hoverColor: "",
            bgColor: "hover:bg-gray-200/50 dark:hover:bg-slate-700/50"
          },
          {
            icon: <FaXTwitter />,
            link: "https://x.com/PiyushJoshi0049",
            label: "Twitter",
            colorClass: "text-gray-900 dark:text-gray-100",
            hoverColor: "",
            bgColor: "hover:bg-gray-200/50 dark:hover:bg-slate-700/50"
          },
          {
            icon: <SiLeetcode />,
            link: "https://leetcode.com/u/piyush0049/",
            label: "LeetCode",
            colorClass: "text-[#FFA116]",
            hoverColor: "hover:text-[#FFA116]",
            bgColor: "hover:bg-[#FFA116]/10"
          }
        ].map((social, index) => (
          <motion.a
            key={index}
            href={social.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`${social.colorClass} ${social.hoverColor} ${social.bgColor} transition-all duration-300 transform hover:scale-110 p-3 rounded-full `}
            title={social.label}
            variants={itemVariants}
            whileHover={{
              scale: 1.2,
              rotate: [0, -10, 10, -5, 5, 0],
              transition: { duration: 0.5 }
            }}
            whileTap={{ scale: 0.9 }}
          >
            {social.icon}
          </motion.a>
        ))}
      </motion.div>

      {/* Contact Me button */}
      <motion.div
        className="mb-16 sm:mb-16 z-10 relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
      >
        <motion.a
          href="/contactme"
          className="relative inline-flex items-center px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 text-sm sm:text-base font-semibold rounded-full transition-all duration-300 group"
          whileHover={{
            scale: 1.05,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
          }}
          whileTap={{ scale: 0.98 }}
        >
          <span className="relative z-10">
            <FiMail className="mr-2 text-lg inline" />
          </span>

          <span className="relative z-10 tracking-wide">Contact Me</span>
          <motion.span
            className="relative z-10 ml-0 opacity-0 group-hover:ml-2 group-hover:opacity-100 transition-all duration-300"
          >
            →
          </motion.span>
        </motion.a>
      </motion.div>

      {/* "What I'm Doing" section */}
      <motion.div
        className="max-w-5xl md:mt-20 mx-auto z-10 relative"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-bold mb-9 sm:mb-12 text-gray-800 dark:text-gray-100 relative inline-block"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          What I'm Doing
          <motion.span
            className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-24 h-0.5 bg-gray-800 dark:bg-gray-300 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: 96 }}
            transition={{ duration: 0.8, delay: 1.6 }}
          ></motion.span>
        </motion.h2>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          transition={{ delayChildren: 1.6, staggerChildren: 0.2 }}
        >
          {[
            {
              icon: <FaLaptopCode className="text-lg sm:text-3xl text-gray-700 dark:text-gray-300" />,
              title: "Frontend Development",
              description: "Crafting responsive and dynamic user interfaces.",
            },
            {
              icon: <FaDatabase className="text-lg sm:text-3xl text-gray-700 dark:text-gray-300" />,
              title: "Backend Development",
              description: "Building robust and secure backend systems.",
            },
            {
              icon: <FaPalette className="text-lg sm:text-3xl text-gray-700 dark:text-gray-300" />,
              title: "UI/UX Design",
              description: "Designing intuitive and visually stunning interfaces.",
            },
            {
              icon: <FaCodeBranch className="text-lg sm:text-3xl text-gray-700 dark:text-gray-300" />,
              title: "Open Source",
              description: "Contributing to open-source projects like Jenkins, RabbitMQ, and Mastra AI.",
            },
            {
              icon: <FaCogs className="text-lg sm:text-3xl text-gray-700 dark:text-gray-300" />,
              title: "DevOps",
              description: "Automating and optimizing development workflows",
            },
            {
              icon: <FaCode className="text-lg sm:text-3xl text-gray-700 dark:text-gray-300" />,
              title: "Data Structures (DSA)",
              description: "Solving problems with efficient algorithms and data structures.",
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                y: -8,
                transition: { duration: 0.3 }
              }}
            >
              <div className="p-6 border border-gray-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-lg shadow-sm hover:shadow-md transition-all duration-500 group relative overflow-hidden">
                <div className="relative z-10">
                  <div className="bg-gray-100 dark:bg-slate-700 p-3 rounded-lg inline-block mb-2 group-hover:bg-gray-200 dark:group-hover:bg-slate-600 transition-colors duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-gray-800 dark:text-gray-100 text-sm sm:text-xl font-semibold mb-1.5 sm:mb-3 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">{item.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Home;
